/** Empty-focus dropdown: the visitor's recent searches from localStorage.
 *  Shown when the search input is focused with no text — the stored history
 *  (written by addToSearchHistory on every submit) finally gets a surface.
 *  Reuses the .ts-autocomplete shell so existing desktop + touch styling
 *  (44px rows, dvh height caps) applies unchanged.
 *
 *  Each row pairs the select action with a per-row remove (×) button, so
 *  the row itself is a flex <div> (a button can't nest a button) carrying
 *  the same .ts-autocomplete-item class the old row-button used — hover,
 *  selected state, and touch sizing apply unchanged. */

import { h } from 'preact';

interface RecentSearchesProps {
  items: string[];
  selectedIndex: number;
  onSelect: (query: string) => void;
  onClear: () => void;
  onRemove: (query: string) => void;
}

export function RecentSearches({ items, selectedIndex, onSelect, onClear, onRemove }: RecentSearchesProps) {
  if (items.length === 0) return null;

  return (
    <div class="ts-autocomplete ts-autocomplete--recents" id="ts-autocomplete-list" role="listbox" aria-label="Recent searches">
      <div class="ts-autocomplete-section">
        <div class="ts-autocomplete-section-head">
          <div class="ts-autocomplete-section-title">Recent Searches</div>
          <button
            type="button"
            class="ts-autocomplete-clear-history"
            onClick={onClear}
            aria-label="Clear recent searches"
          >
            Clear
          </button>
        </div>
        {items.map((q, i) => (
          <div
            key={`recent-${q}`}
            class={`ts-autocomplete-item ts-autocomplete-item--recent ${i === selectedIndex ? 'ts-autocomplete-item--selected' : ''}`}
            role="option"
            aria-selected={i === selectedIndex}
          >
            <button
              type="button"
              class="ts-autocomplete-item-main"
              onClick={() => onSelect(q)}
            >
              <svg class="ts-autocomplete-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
              </svg>
              <span class="ts-autocomplete-item-text">{q}</span>
            </button>
            <button
              type="button"
              class="ts-autocomplete-item-remove"
              onClick={() => onRemove(q)}
              aria-label={`Remove "${q}" from recent searches`}
              title="Remove from recent searches"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
